import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, Loader2, Lock, LogOut, RefreshCw } from "lucide-react";
import Seo from "@/components/Seo";
import { FadeIn } from "@/components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "pct_admin_token";

const AdminPage = () => {
  const [token, setToken] = useState(() => sessionStorage.getItem(TOKEN_KEY) || "");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [messages, setMessages] = useState(null);

  const load = useCallback(async (activeToken) => {
    setBusy(true);
    try {
      const { data } = await axios.get(`${API}/admin/messages`, {
        headers: { Authorization: `Bearer ${activeToken}` },
      });
      setMessages(data);
      setError("");
    } catch (e) {
      sessionStorage.removeItem(TOKEN_KEY);
      setToken("");
      setMessages(null);
      setError("Session expired. Please sign in again.");
    } finally {
      setBusy(false);
    }
  }, []);

  useEffect(() => {
    if (token) load(token);
  }, [token, load]);

  const onLogin = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const { data } = await axios.post(`${API}/admin/login`, { password });
      sessionStorage.setItem(TOKEN_KEY, data.token);
      setToken(data.token);
      setPassword("");
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Something went wrong. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  const onLogout = () => {
    sessionStorage.removeItem(TOKEN_KEY);
    setToken("");
    setMessages(null);
  };

  return (
    <div data-testid="admin-page" className="relative min-h-screen overflow-hidden bg-coal">
      <Seo
        title="Owner Access — Proxima Centauri Tools"
        siteName="Proxima Centauri Tools"
        description="Private area."
      />

      <div className="absolute inset-0" aria-hidden="true">
        <div className="absolute inset-0 bg-[#0b0b0e]" />
        <div className="brushed-metal absolute inset-0 opacity-70" />
        <div className="absolute inset-0 bg-[radial-gradient(50%_35%_at_50%_0%,rgba(230,0,0,0.10),transparent_70%)]" />
      </div>

      <header className="relative z-20 mx-auto flex max-w-[1200px] items-center justify-between px-6 py-6 md:px-12">
        <Link to="/" data-testid="admin-logo-link" className="flex items-center gap-3" aria-label="Proxima Centauri Tools — home">
          <img src="/logo.png" alt="Proxima Centauri Tools logo" className="h-12 w-12 object-cover" />
          <span className="hidden flex-col leading-none sm:flex">
            <span className="font-display text-xl font-semibold uppercase tracking-wider text-white">Proxima Centauri</span>
            <span className="font-display text-sm uppercase tracking-[0.5em] text-ember">Tools</span>
          </span>
        </Link>
        {token ? (
          <button
            data-testid="admin-logout-button"
            onClick={onLogout}
            className="flex items-center gap-2 font-display text-lg uppercase tracking-[0.18em] text-ash transition-colors duration-300 hover:text-white"
          >
            <LogOut size={18} className="text-ember" /> Sign Out
          </button>
        ) : (
          <Link
            to="/"
            data-testid="admin-back-link"
            className="flex items-center gap-2 font-display text-lg uppercase tracking-[0.18em] text-ash transition-colors duration-300 hover:text-white"
          >
            <ArrowLeft size={18} className="text-ember" /> Back To Site
          </Link>
        )}
      </header>

      <main className="relative z-10 mx-auto max-w-[1200px] px-6 pb-28 pt-10 md:px-12 md:pt-16">
        {!token ? (
          <FadeIn className="mx-auto max-w-md">
            <form
              data-testid="admin-login-form"
              onSubmit={onLogin}
              className="border border-white/10 bg-carbon/70 p-10 backdrop-blur-md"
            >
              <span className="flex h-12 w-12 items-center justify-center border border-ember/60 red-glow-box">
                <Lock size={20} className="text-ember" />
              </span>
              <h1 className="mt-8 font-display text-5xl font-semibold uppercase leading-none text-white">
                Owner Access
              </h1>
              <p className="mt-4 font-body text-sm font-light leading-relaxed text-ash">
                Private area. Enter the owner password to read saved enquiries.
              </p>
              <label className="mt-10 block">
                <span className="font-body text-[11px] font-medium uppercase tracking-[0.3em] text-ash">Password</span>
                <input
                  data-testid="admin-password-input"
                  type="password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Owner password"
                  className="w-full border-b border-white/20 bg-transparent py-4 font-body text-base text-white placeholder:text-ash/50 transition-[border-color] duration-300 focus:border-ember focus:outline-none"
                />
              </label>
              {error && (
                <p data-testid="admin-login-error" className="mt-5 font-body text-sm text-ember">
                  {error}
                </p>
              )}
              <button
                data-testid="admin-login-submit"
                type="submit"
                disabled={busy}
                className="mt-10 flex w-full items-center justify-center gap-3 bg-ember px-8 py-4 font-display text-xl font-semibold uppercase tracking-[0.15em] text-white transition-[box-shadow,opacity] duration-300 hover:shadow-[0_0_44px_-6px_rgba(230,0,0,0.65)] disabled:opacity-60"
              >
                {busy && <Loader2 size={18} className="animate-spin" />}
                {busy ? "Checking" : "Enter"}
              </button>
            </form>
          </FadeIn>
        ) : (
          <>
            <div className="flex flex-wrap items-end justify-between gap-6">
              <div>
                <p className="flex items-center gap-4 font-body text-xs font-medium uppercase tracking-[0.35em] text-ash">
                  <span className="font-display text-xl text-ember">ENQ</span>
                  <span className="h-px w-12 bg-ember/70" />
                  Private Inbox
                </p>
                <h1 data-testid="admin-inbox-heading" className="mt-6 font-display text-6xl font-semibold uppercase leading-[0.9] text-white md:text-7xl">
                  Enquiries<span className="text-ember">.</span>
                </h1>
              </div>
              <button
                data-testid="admin-refresh-button"
                onClick={() => load(token)}
                className="flex items-center gap-2 border border-steel/30 px-6 py-3 font-display text-lg font-semibold uppercase tracking-[0.15em] text-steel transition-[border-color,color] duration-300 hover:border-ember hover:text-white"
              >
                <RefreshCw size={16} className={busy ? "animate-spin" : ""} /> Refresh
              </button>
            </div>

            <div className="mt-14 flex flex-col gap-4">
              {messages === null ? (
                <p data-testid="admin-loading" className="font-body text-sm text-ash">Loading enquiries…</p>
              ) : messages.length === 0 ? (
                <div data-testid="admin-empty" className="border border-dashed border-white/15 p-14 text-center">
                  <p className="font-display text-3xl font-semibold uppercase text-white">No Enquiries Yet</p>
                  <p className="mt-3 font-body text-sm font-light text-ash">
                    Messages sent through the contact form will appear here.
                  </p>
                </div>
              ) : (
                messages.map((m) => (
                  <FadeIn key={m.id}>
                    <article data-testid={`admin-message-${m.id}`} className="border border-white/10 bg-carbon/70 p-7 md:p-8">
                      <div className="flex flex-wrap items-center justify-between gap-3">
                        <div className="flex flex-wrap items-center gap-4">
                          <h2 className="font-display text-2xl font-semibold uppercase leading-none text-white">{m.name}</h2>
                          <a href={`mailto:${m.email}`} className="font-body text-sm text-ember transition-colors hover:text-white">
                            {m.email}
                          </a>
                          {m.company && <span className="font-body text-sm text-ash">{m.company}</span>}
                        </div>
                        <div className="flex items-center gap-4">
                          <span
                            className={`font-body text-[10px] uppercase tracking-[0.3em] ${m.emailed ? "text-ember" : "text-ash/60"}`}
                          >
                            {m.emailed ? "Emailed" : "Saved"}
                          </span>
                          {m.auto_replied && (
                            <span className="font-body text-[10px] uppercase tracking-[0.3em] text-steel/70">Auto-replied</span>
                          )}
                          <span className="font-body text-xs text-ash/70">
                            {new Date(m.created_at).toLocaleString()}
                          </span>
                        </div>
                      </div>
                      <p className="mt-4 whitespace-pre-wrap font-body text-sm font-light leading-relaxed text-ash">
                        {m.message}
                      </p>
                    </article>
                  </FadeIn>
                ))
              )}
            </div>
          </>
        )}
      </main>
    </div>
  );
};

export default AdminPage;
